import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { WeightCaptureNode as WeightCaptureNodeType, PGD, DoseBand } from "@/lib/pharmacyFirst";

interface WeightCaptureNodeProps {
  node: WeightCaptureNodeType;
  isDarkMode?: boolean;
  onAnswer: (weightKg: number) => void;
  value?: number | null;
  stepNumber?: number;
  pgd?: PGD;
}

function resolvedBandForWeight(pgd: PGD, weightKg: number): DoseBand | null {
  for (const band of pgd.doseBands) {
    if (band.resolveBy === "weight" && band.weightRange) {
      if (weightKg >= band.weightRange.minKg && weightKg <= band.weightRange.maxKg) {
        return band;
      }
    }
  }
  return null;
}

function formatDose(band: DoseBand, weightKg?: number): string {
  if (band.mgPerKg && weightKg) {
    const mg = Math.round(weightKg * band.mgPerKg * 10) / 10;
    return `${mg}mg (${band.mgPerKg} mg/kg)`;
  }
  return `${band.dose}mg`;
}

export function WeightCaptureNode({
  node,
  isDarkMode = false,
  onAnswer,
  value,
  stepNumber,
  pgd,
}: WeightCaptureNodeProps) {
  const [text, setText] = useState<string>(value ? String(value) : "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setText(value ? String(value) : "");
    setError(null);
  }, [node.id]);

  const weightKg = (() => {
    const n = Number(text.trim());
    return Number.isFinite(n) && n > 0 ? n : null;
  })();

  const weightBands = pgd?.doseBands.filter((b) => b.resolveBy === "weight") ?? [];
  const activeBand = pgd && weightKg ? resolvedBandForWeight(pgd, weightKg) : null;

  const handleSubmit = () => {
    const trimmed = text.trim();
    if (!trimmed) {
      setError("Please enter a weight in kilograms.");
      return;
    }
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n <= 0) {
      setError("Enter a valid positive number.");
      return;
    }
    if (typeof node.minKg === "number" && n < node.minKg) {
      setError(`Weight must be at least ${node.minKg}kg.`);
      return;
    }
    if (typeof node.maxKg === "number" && n > node.maxKg) {
      setError(`Weight must be no more than ${node.maxKg}kg.`);
      return;
    }
    onAnswer(n);
  };

  return (
    <div
      className={`border rounded-2xl p-4 ${
        isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
      }`}
    >
      {stepNumber !== undefined && (
        <div className="flex items-center gap-3 mb-3">
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold ${
              isDarkMode ? "bg-white text-black" : "bg-black text-white"
            }`}
          >
            {stepNumber}
          </div>
          <h3
            className={`text-sm font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {node.text}
          </h3>
        </div>
      )}

      {node.hint && (
        <p
          className={`text-xs mb-3 ${
            isDarkMode ? "text-gray-400" : "text-gray-600"
          }`}
        >
          {node.hint}
        </p>
      )}

      <div className="flex items-end gap-3">
        <div className="flex-1">
          <Label
            htmlFor={`${node.id}-weight`}
            className={`text-xs ${
              isDarkMode ? "text-gray-300" : "text-gray-700"
            }`}
          >
            Weight (kg)
          </Label>
          <Input
            id={`${node.id}-weight`}
            type="number"
            inputMode="decimal"
            min={node.minKg}
            max={node.maxKg}
            step="0.1"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSubmit();
            }}
            className="mt-1"
          />
        </div>
        <Button onClick={handleSubmit}>Continue</Button>
      </div>

      {error && (
        <p className="mt-2 text-xs text-red-600">{error}</p>
      )}

      {weightBands.length > 0 && (
        <div className="mt-4">
          <p className={`text-xs font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
            Dose bands
          </p>
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full text-xs">
              <thead>
                <tr className={isDarkMode ? "bg-gray-700" : "bg-gray-50"}>
                  <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>Weight range</th>
                  <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>Dose</th>
                  <th className={`px-3 py-2 text-left font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>Frequency</th>
                </tr>
              </thead>
              <tbody>
                {weightBands.map((band) => {
                  const isActive = activeBand?.id === band.id;
                  return (
                    <tr
                      key={band.id}
                      className={
                        isActive
                          ? "bg-blue-50 border-l-2 border-l-blue-500"
                          : isDarkMode
                          ? "border-t border-gray-600"
                          : "border-t border-gray-100"
                      }
                    >
                      <td className={`px-3 py-2 font-medium ${isActive ? "text-blue-800" : isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
                        {band.weightRange!.minKg}–{band.weightRange!.maxKg}kg
                        {isActive && (
                          <span className="ml-2 inline-block px-1.5 py-0.5 rounded-full text-[10px] bg-blue-100 text-blue-700 font-semibold">
                            Selected
                          </span>
                        )}
                      </td>
                      <td className={`px-3 py-2 ${isActive ? "text-blue-800 font-semibold" : isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                        {formatDose(band, isActive && weightKg ? weightKg : undefined)}
                      </td>
                      <td className={`px-3 py-2 ${isActive ? "text-blue-800" : isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                        {band.frequency}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {weightKg && !activeBand && (
            <p className="mt-1 text-xs text-amber-600">
              No weight band matches {weightKg}kg — check if this patient should use the age-based dose.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
